import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Result,
  Row,
  Select,
  Space,
  Typography,
  notification,
} from 'antd';
import { ReloadOutlined, SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { isPlatformOperatorUser } from '../../assets/utils/permissions';
import DashboardSection from '../dashboard/components/DashboardSection';

const { Text, Paragraph, Title } = Typography;

const CHARSET_OPTIONS = [
  { value: 'numeric', label: 'Numeric only (0–9)' },
  { value: 'alphanumeric', label: 'Alphanumeric (A–Z, 2–9 — no 0/O/1/I)' },
];

// Mirrors backend IdentityGenerationSettingsService bounds.
const MIN_LENGTH = 4;
const MAX_LENGTH = 40;
const MIN_ATTEMPTS = 1;
const MAX_ATTEMPTS = 100;

function idSpaceLabel(length, charset) {
  const base = charset === 'alphanumeric' ? 32 : 10;
  const len = Number(length) || 0;
  if (len <= 0) return '—';
  // Rough order-of-magnitude only — exact BigInt math isn't worth it here.
  const exponent = len * Math.log10(base);
  return `~10^${exponent.toFixed(1)} possible values`;
}

const IdentityGenerationSettingsPage = () => {
  const { user } = useAuth();
  const allowed = useMemo(() => isPlatformOperatorUser(user), [user]);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [meta, setMeta] = useState({ updated_by: null, updated_at: null });
  const [sample, setSample] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getIdentityGenerationSettings();
      if (resp?.error) return;
      const data = resp?.data?.data || {};
      form.setFieldsValue({
        policy_no_length: data.policy_no_length,
        policy_no_charset: data.policy_no_charset,
        insurance_no_length: data.insurance_no_length,
        insurance_no_charset: data.insurance_no_charset,
        max_generation_attempts: data.max_generation_attempts,
      });
      setMeta({ updated_by: data.updated_by, updated_at: data.updated_at });
      setSample(null);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const runPreview = async () => {
    try {
      const values = await form.validateFields();
      setPreviewing(true);
      const resp = await lib.previewIdentityGenerationSettings(values);
      if (resp?.error) return;
      setSample(resp?.data?.data || null);
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const resp = await lib.updateIdentityGenerationSettings(values);
      if (resp?.error) return;
      notification.success({
        message: resp?.data?.message || 'Identity generation settings saved',
        description: 'Applies to new accounts and enrollees from now on — nothing existing was touched.',
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Platform operators only"
        subTitle="policy_no / insurance_no generation settings are managed by platform operators (platform.*) — not even admin.*."
      />
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 780, margin: '0 auto' }}>
      <Title level={3} style={{ marginTop: 0 }}>Identity generation settings</Title>
      <Paragraph type="secondary">
        Controls how a brand-new client's policy number and a brand-new enrollee's insurance
        number are generated. Only affects rows created from here forward — existing accounts
        and enrollees keep whatever number they already have, forever.
      </Paragraph>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Changing this immediately affects the next registration"
        description="There is no staged rollout — save takes effect on the very next new account or enrollee. Use Preview first."
      />

      <DashboardSection
        id="identity-generation-settings"
        title="Generation rules"
        subtitle={meta.updated_at ? `Last changed ${meta.updated_at}${meta.updated_by ? ` by staff #${meta.updated_by}` : ''}` : 'Default values — never changed'}
        collapsible={false}
      >
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Title level={5}>Policy number</Title>
              <Form.Item
                name="policy_no_length"
                label="Length"
                rules={[{ required: true, type: 'number', min: MIN_LENGTH, max: MAX_LENGTH }]}
              >
                <InputNumber style={{ width: '100%' }} min={MIN_LENGTH} max={MAX_LENGTH} />
              </Form.Item>
              <Form.Item
                name="policy_no_charset"
                label="Character set"
                rules={[{ required: true }]}
              >
                <Select options={CHARSET_OPTIONS} />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {idSpaceLabel(form.getFieldValue('policy_no_length'), form.getFieldValue('policy_no_charset'))}
                  </Text>
                )}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Title level={5}>Insurance number</Title>
              <Form.Item
                name="insurance_no_length"
                label="Length"
                rules={[{ required: true, type: 'number', min: MIN_LENGTH, max: MAX_LENGTH }]}
              >
                <InputNumber style={{ width: '100%' }} min={MIN_LENGTH} max={MAX_LENGTH} />
              </Form.Item>
              <Form.Item
                name="insurance_no_charset"
                label="Character set"
                rules={[{ required: true }]}
              >
                <Select options={CHARSET_OPTIONS} />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {idSpaceLabel(form.getFieldValue('insurance_no_length'), form.getFieldValue('insurance_no_charset'))}
                  </Text>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item
            name="max_generation_attempts"
            label="Max retry attempts on collision"
            extra="Shared between both fields. Raise this if you've narrowed length/charset enough that collisions become likely at your current member count."
            rules={[{ required: true, type: 'number', min: MIN_ATTEMPTS, max: MAX_ATTEMPTS }]}
            style={{ maxWidth: 320 }}
          >
            <InputNumber style={{ width: '100%' }} min={MIN_ATTEMPTS} max={MAX_ATTEMPTS} />
          </Form.Item>

          <Space wrap style={{ marginTop: 8 }}>
            <Button icon={<ThunderboltOutlined />} onClick={runPreview} loading={previewing}>
              Preview sample
            </Button>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Reload saved values
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={saving}>
              Save
            </Button>
          </Space>

          {sample && (
            <Card size="small" style={{ marginTop: 16, maxWidth: 420 }}>
              <Space direction="vertical" size={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Sample only — not saved, not reserved, will never be issued to a real account.
                </Text>
                <Text>Policy number: <Text code>{sample.policy_no}</Text></Text>
                <Text>Insurance number: <Text code>{sample.insurance_no}</Text></Text>
              </Space>
            </Card>
          )}
        </Form>
      </DashboardSection>
    </div>
  );
};

export default IdentityGenerationSettingsPage;
